'use client';

/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import {
  Button,
  Container,
  Drawer,
  FlexItem,
  FlexLayout,
  Icon,
  Link,
  LinkGroup,
  Logo,
  Separator,
  SocialMediaGroup,
  ToggleSwitch,
} from '@wci-tech/core-ui';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { BsArrowUpRight } from 'react-icons/bs';
import { GiHamburgerMenu } from 'react-icons/gi';
import { GoMoon } from 'react-icons/go';
import { IoClose } from 'react-icons/io5';
import { MdOutlineLightMode } from 'react-icons/md';

import { appConfig } from '@/config/appConfig';
import { FaIcons } from '@/constants/faIcons';

import {
  handleInstagramClick,
  handleLinkedInClick,
  handleTwitterClick,
  handleYouTubeClick,
} from '@/constants/socialMediaClick';

import { useThemeMode } from '@/context';
import { BookADemoButton } from '../book-a-demo-button';
import { useStyles } from './Navbar.styles';

type NavbarProps = {
  isHeaderMode?: boolean;
  showMobileNavigation?: boolean;
  showHeaderToggle?: boolean;
  scrolled?: boolean;
  animateSticky?: boolean;
};

const NAVBAR_SCROLL_OFFSET = 72;
const MOBILE_SCROLL_SPY_BUFFER = 24;

const Navbar = ({
  isHeaderMode = false,
  showMobileNavigation = true,
  showHeaderToggle = true,
  scrolled = false,
  animateSticky = false,
}: NavbarProps) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const router = useRouter();
  const { isDark, toggleTheme } = useThemeMode();
  const styles = useStyles(scrolled);
  const t = useTranslations('Navbar');

  const navigationLinks = useMemo(
    () => [
      {
        url: 'home',
        label: t('links.home'),
      },
      {
        url: 'what-we-provide',
        label: t('links.whatWeProvide'),
      },
      {
        url: 'why-it-works',
        label: t('links.whyItWorks'),
      },
      {
        url: 'how-it-works',
        label: t('links.howItWorks'),
      },
      {
        url: 'who-its-for',
        label: t('links.whoItsFor'),
      },
      {
        url: 'case-studies',
        label: t('links.caseStudies'),
        redirectUrl: '/coming-soon',
      },
      {
        url: 'about',
        label: t('links.about'),
      },
      {
        url: 'contact',
        label: t('links.contact'),
      },
      {
        url: 'careers',
        label: t('links.careers'),
        redirectUrl: '/coming-soon',
      },
    ],
    [t],
  );

  useEffect(() => {
    const handleScrollSpy = () => {
      const scrollPosition = window.scrollY + NAVBAR_SCROLL_OFFSET + MOBILE_SCROLL_SPY_BUFFER;

      let currentSection = 'home';

      navigationLinks.forEach((link) => {
        if (link.redirectUrl) {
          return;
        }

        const section = document.getElementById(link.url);

        if (!section) {
          return;
        }

        if (scrollPosition >= section.offsetTop) {
          currentSection = link.url;
        }
      });

      setActiveSection((current) => (current === currentSection ? current : currentSection));
    };

    handleScrollSpy();

    window.addEventListener('scroll', handleScrollSpy, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScrollSpy);
    };
  }, [navigationLinks]);

  const handleScrollTo = (id: string) => {
    const selectedLink = navigationLinks.find((link) => link.url === id);

    setMobileNavOpen(false);

    if (selectedLink?.redirectUrl) {
      router.push(selectedLink.redirectUrl);

      return;
    }

    setActiveSection(id);

    if (id === 'home') {
      window.history.replaceState(null, '', window.location.pathname);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      return;
    }

    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(elementTop - NAVBAR_SCROLL_OFFSET, 0);

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });

    window.history.replaceState(null, '', `#${id}`);
  };

  const handleDesktopNavigationClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');

    if (!anchor) {
      return;
    }

    const text = anchor.textContent?.trim();

    if (text === t('links.home')) {
      event.preventDefault();
      event.stopPropagation();

      handleScrollTo('home');

      return;
    }

    if (text === t('links.caseStudies') || text === t('links.careers')) {
      event.preventDefault();
      event.stopPropagation();

      router.push('/coming-soon');
    }
  };

  const mobileNavBody = (
    <FlexLayout
      direction="column"
      gap={{
        default: 0,
      }}
      padding={{
        default: 0,
      }}
      alignItems="center"
    >
      <FlexItem css={{ width: '100%' }}>
        <FlexLayout
          direction="column"
          padding={{
            default: 0,
          }}
          gap={{
            default: 0,
          }}
        >
          {navigationLinks.map((link) => {
            const isActive = link.url === activeSection;

            return (
              <FlexItem key={link.url}>
                <div
                  css={styles.drawerLinkWrapper(isActive)}
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => handleScrollTo(link.url)}
                >
                  <FlexLayout
                    alignItems="center"
                    justifyContent="space-between"
                    padding={{
                      default: '16px',
                    }}
                    direction={{
                      default: 'row',
                    }}
                  >
                    <FlexItem>
                      <Link
                        url={link.redirectUrl ?? (link.url === 'home' ? '#' : `#${link.url}`)}
                        target="_self"
                        label={link.label}
                        effect="none"
                        customStyles={{
                          link: styles.drawerLinkText(isActive),
                        }}
                        onClick={(event) => {
                          event.preventDefault();

                          handleScrollTo(link.url);
                        }}
                      />
                    </FlexItem>

                    <FlexItem>
                      <Icon
                        name={BsArrowUpRight}
                        size={20}
                        color={isActive ? '#ffffff' : 'var(--brand-color)'}
                      />
                    </FlexItem>
                  </FlexLayout>
                </div>
              </FlexItem>
            );
          })}
        </FlexLayout>
      </FlexItem>

      <FlexItem
        css={{
          width: '100%',
          padding: '24px 16px',
        }}
      >
        <Separator orientation="horizontal" />
      </FlexItem>

      <FlexItem
        css={{
          marginBottom: '20px',
        }}
      >
        <SocialMediaGroup
          platforms={[
            {
              frontIcon: {
                name: FaIcons.LinkedIn,
              },
              url: 'https://www.linkedin.com/company/wealthcraft-innovations',
              onClick: handleLinkedInClick,
            },
            {
              frontIcon: {
                name: FaIcons.Instagram,
              },
              url: 'https://www.instagram.com/wealthcraftinnovations',
              onClick: handleInstagramClick,
            },
            {
              frontIcon: {
                name: FaIcons.Twitter,
              },
              url: 'https://x.com/wci_tech',
              onClick: handleTwitterClick,
            },
            {
              frontIcon: {
                name: FaIcons.youTube,
              },
              url: 'https://www.youtube.com/@wealthcraftinnovations2024',
              onClick: handleYouTubeClick,
            },
          ]}
          color={isDark ? '#ffffff' : '#182748'}
          size={24}
          customStyles={{
            wrapper: css`
              background-color: transparent;
              padding: 0;

              display: flex;
              align-items: center;
              justify-content: center;
            `,

            container: styles.socialMediaContainer,
          }}
        />
      </FlexItem>

      <FlexItem
        css={{
          padding: '0 16px 32px 16px',

          width: '100%',
        }}
      >
        <FlexLayout
          justifyContent="center"
          padding={{
            default: 0,
          }}
        >
          <BookADemoButton
            shape="pill"
            variant="linear-gradient"
            buttonText={t('bookDemo')}
            customStyles={{
              base: css`
                ${styles.headerBookADemo};
              `,
            }}
            calendlyUrl={appConfig.integrations.calendly.eventUrl}
          />
        </FlexLayout>
      </FlexItem>
    </FlexLayout>
  );

  const navbarClassName = [scrolled ? 'scrolled' : '', animateSticky ? 'animate-sticky' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {showMobileNavigation && (
        <div inert={!mobileNavOpen ? true : undefined}>
          <Drawer
            body={mobileNavBody}
            isOpen={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            position="right"
            drawerSize={320}
            closeIcon={<Icon name={IoClose} size={20} color={isDark ? '#ffffff' : '#000000'} />}
            customStyles={{
              drawer: styles.drawer,

              body: css`
                padding: 0;
                margin-top: 76px;
                overflow-x: hidden;
              `,
            }}
          />
        </div>
      )}

      {isHeaderMode ? (
        <FlexLayout
          alignItems="center"
          direction="row"
          gap={16}
          padding={{
            default: 0,
          }}
        >
          {showHeaderToggle && (
            <FlexItem>
              <ToggleSwitch
                id="mobile-header-theme-toggle-switch"
                onIcon={<Icon name={GoMoon} size={12} color="white" />}
                offIcon={<Icon name={MdOutlineLightMode} size={12} color="black" />}
                checked={isDark}
                onChange={(checked) => toggleTheme(checked)}
                size={60}
                customStyles={{
                  wrapper: styles.toggleWrapper,

                  track: styles.track,

                  thumb: css`
                    background-color: ${isDark ? 'black' : 'white'};
                  `,
                }}
              />
            </FlexItem>
          )}

          {showMobileNavigation && (
            <FlexItem>
              <Button
                onClick={() => setMobileNavOpen(true)}
                ariaLabel="toggle"
                icon={<Icon name={GiHamburgerMenu} size={28} color="var(--text-color)" />}
                appearance="ghost"
                customStyles={{
                  base: styles.hamburgerButton,
                }}
              />
            </FlexItem>
          )}
        </FlexLayout>
      ) : (
        <nav css={styles.navbar} className={navbarClassName}>
          <Container css={styles.container}>
            <div css={styles.navWrapper}>
              <div
                className="sticky-logo"
                css={styles.sideLogo}
                onClick={() => handleScrollTo('home')}
                style={{
                  cursor: 'pointer',
                }}
              >
                <Logo
                  src="/images/monoLogo.png"
                  decorative={false}
                  alt={t('logoAlt')}
                  priority
                  fetchPriority="high"
                  width={58}
                  height={50}
                  href="/"
                />
              </div>

              <div css={styles.navLinks} onClickCapture={handleDesktopNavigationClick}>
                <LinkGroup
                  customStyles={{
                    slider: css`
                      background-color: var(--brand-color);
                    `,

                    underlineSlider: css`
                      background-color: var(--brand-color);
                    `,

                    link: css`
                      color: var(--text-color);

                      &::after {
                        background: var(--brand-color);
                      }
                    `,

                    list: styles.list,
                  }}
                  data={navigationLinks.map((link) => ({
                    url: link.redirectUrl ? `${link.redirectUrl}?source=${link.url}` : link.url,

                    label: link.label,
                  }))}
                  offset={NAVBAR_SCROLL_OFFSET}
                />
              </div>

              <div css={styles.rightControls}>
                <ToggleSwitch
                  id="sticky-navbar-theme-toggle-switch"
                  onIcon={<Icon name={GoMoon} size={12} color="white" />}
                  offIcon={<Icon name={MdOutlineLightMode} size={12} color="black" />}
                  checked={isDark}
                  onChange={(checked) => toggleTheme(checked)}
                  size={60}
                  customStyles={{
                    wrapper: styles.toggleWrapper,

                    track: styles.track,

                    thumb: css`
                      background-color: ${isDark ? 'black' : 'white'};
                    `,
                  }}
                />

                <div css={styles.stickyHamburger}>
                  <Button
                    onClick={() => setMobileNavOpen(true)}
                    ariaLabel="toggle navigation"
                    icon={<Icon name={GiHamburgerMenu} size={28} color="var(--text-color)" />}
                    customStyles={{
                      base: styles.hamburgerButton,
                    }}
                  />
                </div>
              </div>
            </div>
          </Container>
        </nav>
      )}
    </>
  );
};

export default Navbar;
